import { useEffect, useRef, useState } from 'react';
import ProgressBar from '@/features/LoadingScreen/anexes/ProgressBar';
import '@/features/LoadingScreen/styles/index.scss';
import { DUMMY_PROGRESS_INTERVAL_MS } from '@/utilities/constants/clientTimeouts';
import { useProgressSync } from '@/features/LoadingScreen/useSyncProgress';

import { setGameStatus } from '@/store/actions';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';

const LoadingBar = ({
  isStuckedLoading,
  onAnimationFinish,
}: LoadingBarProps) => {
  const [displayProgress, setDisplayProgress] = useState(
    () => useStore.getState().gameStatus.internalLoadProgress,
  );
  const displayProgressRef = useRef(displayProgress);
  const lastSyncedProgressRef = useRef(-1);

  useEffect(() => {
    displayProgressRef.current = displayProgress;
  }, [displayProgress]);

  const {
    gameLoadProgress,
    gameLoadStarted,
    gameLoadSuccess,
    gameFetchStarted,
    gameFetchSuccess,
  } = useStore(
    useShallow((s) => ({
      gameLoadProgress: s.gameStatus.gameLoadProgress,
      gameLoadStarted: s.gameStatus.gameLoadStarted,
      gameLoadSuccess: s.gameStatus.gameLoadSuccess,
      gameFetchSuccess: s.gameStatus.gameFetchSuccess,
      gameFetchStarted: s.gameStatus.gameFetchStarted,
    })),
  );

  const {
    startDummy,
    applyGameProgress,
    scheduleIdle,
    completeProgress,
    cleanup,
  } = useProgressSync(setDisplayProgress);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    const rounded = Math.round(displayProgress);
    const crossedNinetyThreshold =
      lastSyncedProgressRef.current < 90 && displayProgress >= 90;

    if (
      rounded === lastSyncedProgressRef.current &&
      !crossedNinetyThreshold &&
      displayProgress < 100
    ) {
      return;
    }

    lastSyncedProgressRef.current = rounded;
    setGameStatus({ internalLoadProgress: displayProgress });
  }, [displayProgress]);

  useEffect(() => {
    // finalization: animate to 100% then call onAnimationFinish
    if (gameLoadSuccess || isStuckedLoading) {
      completeProgress(onAnimationFinish);
      return;
    }

    // While doing GLS call -> nudge slower
    if (gameFetchStarted && !gameFetchSuccess) {
      startDummy(DUMMY_PROGRESS_INTERVAL_MS * 2);
      return;
    }
    // real progress arrived and is ahead of displayed -> apply immediately (never decrease)
    if (gameLoadStarted && gameLoadProgress > displayProgressRef.current) {
      applyGameProgress(gameLoadProgress);
      return;
    }

    // game started but reported progress is <= displayed -> keep slow nudges, schedule idle
    if (gameLoadStarted && gameLoadProgress !== 0) {
      scheduleIdle();
      return;
    }

    // no real progress yet -> fast dummy
    startDummy(DUMMY_PROGRESS_INTERVAL_MS);
  }, [
    gameLoadStarted,
    gameLoadProgress,
    gameLoadSuccess,
    gameFetchStarted,
    gameFetchSuccess,
    isStuckedLoading,
    onAnimationFinish,
    applyGameProgress,
    scheduleIdle,
    startDummy,
    completeProgress,
  ]);

  return (
    <div className="progress-container" data-testid="loading-progress-bar">
      <ProgressBar displayProgress={displayProgress} />
    </div>
  );
};

export default LoadingBar;

export type LoadingBarProps = {
  isStuckedLoading: boolean;
  onAnimationFinish?: () => void;
};
